import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Save, Sparkles, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { SkeletonForm } from '@/components/ui/skeleton';
import { useToast } from '@/contexts/ToastContext';
import { useApiClient } from '@/hooks/useApiClient';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { logError } from '@/lib/logger';
import type { ApiClient } from '@/lib/api';
import {
  createRuleFromMarkers,
  describeCondition,
  getCategoryForMarker,
  QUALITY_MARKER_RULES,
} from '@/lib/rulePatterns';
import { IncidentModal } from '../../modals/IncidentModal';
import { QUALITY_MARKER_KEYS } from './analysis-utils';

interface AgentReviewSectionProps {
  analysisId: string;
  recordId?: string;
  projectId?: string;
  /**
   * Caller-supplied apiClient (e.g. the embedded-mode client the caller
   * already resolved via useRecordDetail()). When omitted, falls back to
   * useApiClient()'s Clerk-based client — see AgentReviewSectionWithHookClient
   * below, the ONLY place that hook is called, so it never runs where
   * there is no ClerkProvider. Embedded (Bitrix24) mode explicitly skips
   * ClerkProvider (main.tsx `isEmbeddedRoute`), and useApiClient() ->
   * useAuthFetch() -> useClerk() throws without one — calling it
   * unconditionally here would crash the embedded record-detail panel.
   * Same precedent as IncidentModal / AnnotationModal.
   */
  apiClient?: ApiClient;
  /**
   * Caller-supplied global role — mirrors RecordDetailInline's own
   * `userRole` prop (a plain string in both standalone and embedded mode,
   * never a usePermissions()/useRole() call, which — like useApiClient() —
   * requires RoleProvider and is not mounted on embedded routes).
   *
   * Used only to gate "Create Rule from Pattern": POST /api/v1/alerts/rules
   * requires checkPermission(auth, 'update', {type: 'project', ...}), i.e.
   * PERMISSION_MATRIX.project.update — roles: ['client', 'admin'] (see
   * apps/edge-api/src/routes/alerts.ts). The review-fetch/save and
   * create-incident actions both require only 'read' on 'analysis'
   * (PERMISSION_MATRIX.analysis.read — client/admin org-wide,
   * supervisor/agent with project access), which is the same or a looser
   * bar than reaching this record's detail panel already required — so no
   * separate gate is needed for those two.
   *
   * Doesn't reproduce the PERMISSION_MATRIX's `projectAccessFallback:
   * 'admin'` case (a per-project 'admin' role independent of global role):
   * RecordDetailInline has no per-project role plumbing to this component,
   * same simplification as its existing `isAgent`/`canCreateIncident` gates.
   */
  userRole?: string | null;
}

export function AgentReviewSection({
  apiClient: apiClientProp,
  ...props
}: AgentReviewSectionProps) {
  if (apiClientProp) {
    return <AgentReviewSectionView {...props} apiClient={apiClientProp} />;
  }
  return <AgentReviewSectionWithHookClient {...props} />;
}

// Isolates the useApiClient() call so it is only ever mounted when the
// caller did NOT supply an apiClient — never in embedded mode, where the
// caller always supplies one.
function AgentReviewSectionWithHookClient(
  props: Omit<AgentReviewSectionProps, 'apiClient'>
) {
  const { apiClient } = useApiClient();
  return <AgentReviewSectionView {...props} apiClient={apiClient} />;
}

interface AgentReviewSectionViewProps extends Omit<AgentReviewSectionProps, 'apiClient'> {
  apiClient: ApiClient;
}

/**
 * Everything the confirmation dialog needs to describe the rule before it
 * exists, and everything handleConfirmCreateRule needs to create it —
 * computed once when the user asks to create a rule, so the dialog and the
 * actual creation never disagree about what markers were selected.
 */
interface PendingRule {
  ruleJson: Record<string, unknown>;
  category: 'quality' | 'compliance' | 'sales' | 'sentiment' | 'custom';
  severity: 'low' | 'medium' | 'high' | 'critical';
  name: string;
  description: string;
}

const SEVERITY_LABELS: Record<PendingRule['severity'], string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

function AgentReviewSectionView({
  analysisId,
  recordId,
  projectId,
  apiClient,
  userRole,
}: AgentReviewSectionViewProps) {
  const { t } = useTranslation(['records', 'common']);
  const { toast } = useToast();
  // POST /api/v1/alerts/rules requires 'update' on the project — client/admin
  // only (see the doc comment on `userRole` above). Fetch/save review and
  // create-incident need no equivalent gate here (see same comment).
  const canCreateAlertRule = userRole === 'client' || userRole === 'admin';
  const [review, setReview] = useState<{
    status: 'pending' | 'reviewed' | 'flagged';
    markers: string[];
    overallFeedback: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [selectedMarkers, setSelectedMarkers] = useState<string[]>([]);
  const [feedback, setFeedback] = useState('');
  const [creatingRule, setCreatingRule] = useState(false);
  const [pendingRule, setPendingRule] = useState<PendingRule | null>(null);
  const [showIncidentModal, setShowIncidentModal] = useState(false);

  // Compute status automatically based on markers and feedback
  const computedStatus: 'pending' | 'reviewed' | 'flagged' =
    selectedMarkers.length > 0
      ? 'flagged'
      : feedback.trim() || review
        ? 'reviewed'
        : 'pending';

  // Fetch review when component mounts or analysisId changes
  useEffect(() => {
    let ignore = false;

    async function fetchReview() {
      try {
        setLoading(true);
        const response = await apiClient.analyses.getAnalysisReview(analysisId);
        if (ignore) return;

        if (response.review) {
          const reviewData = response.review;
          setReview({
            status: reviewData.status,
            markers: reviewData.markers ?? [],
            overallFeedback: reviewData.overallFeedback ?? '',
          });

          if (!Array.isArray(reviewData.markers)) {
            logError('records.agentReview.invalid_markers_response', {
              component: 'AgentReviewSection',
              analysisId,
              markers: reviewData.markers,
            });
            setSelectedMarkers([]);
          } else {
            setSelectedMarkers(reviewData.markers);
          }

          setFeedback(reviewData.overallFeedback || '');
        } else {
          setReview(null);
          setSelectedMarkers([]);
          setFeedback('');
        }
      } catch (err) {
        if (ignore) return;

        logError(
          'records.agentReview.fetch_failed',
          { component: 'AgentReviewSection', analysisId },
          err instanceof Error ? err : undefined
        );
        if (err instanceof Error && !err.message.includes('404')) {
          toast.error(t('records.agentReview.loadReviewError'), err.message);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    void fetchReview();

    return () => {
      ignore = true;
    };
  }, [analysisId, apiClient, toast, t]);

  const handleSaveReview = async () => {
    try {
      setSaving(true);
      const response = await apiClient.analyses.createAnalysisReview(analysisId, {
        status: computedStatus,
        markers: selectedMarkers,
        overallFeedback: feedback.trim() || undefined,
      });

      if (response.review) {
        setReview({
          status: response.review.status,
          markers: response.review.markers ?? [],
          overallFeedback: response.review.overallFeedback ?? '',
        });
        setFeedback('');
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 2000);
        toast.success(
          t('records.agentReview.reviewSaved'),
          t('records.agentReview.reviewSavedDescription')
        );
      }
    } catch (err) {
      logError(
        'records.agentReview.save_failed',
        { component: 'AgentReviewSection', analysisId },
        err instanceof Error ? err : undefined
      );
      toast.error(
        t('records.agentReview.saveReviewError'),
        err instanceof Error ? err.message : t('common:common.errorUnknown')
      );
    } finally {
      setSaving(false);
    }
  };

  const handleMarkerToggle = (markerId: string) => {
    setSelectedMarkers((prev) =>
      prev.includes(markerId) ? prev.filter((id) => id !== markerId) : [...prev, markerId]
    );
  };

  // Builds the rule preview and opens the confirmation dialog. Does NOT
  // create anything — a single click here used to write a permanent,
  // project-wide alert rule (isEnabled: true) with no warning, which is
  // how «Alert: Tone Issues» shipped with a threshold that false-positived
  // on ordinary calls (docs/audit/2026-07-26-alerts-review.md).
  const handleRequestCreateRule = () => {
    if (!projectId || selectedMarkers.length === 0) {
      toast.error(
        t('records.agentReview.cannotCreateRule'),
        t('records.agentReview.selectMarkerFirst')
      );
      return;
    }

    const ruleJson = createRuleFromMarkers(selectedMarkers);
    if (!ruleJson) {
      toast.error(
        t('records.agentReview.cannotCreateRule'),
        t('records.agentReview.ruleGenerationFailed')
      );
      return;
    }

    const firstMarker = selectedMarkers[0];
    if (!firstMarker) return;
    const category = getCategoryForMarker(firstMarker);
    // Severity sits explicitly on each rule (QUALITY_MARKER_RULES) rather
    // than being guessed from a substring in the marker id — see the
    // module doc comment on QualityMarkerRule for why that guess was
    // wrong for this exact marker.
    const severity = QUALITY_MARKER_RULES[firstMarker]?.severity ?? 'medium';

    const markerLabels = selectedMarkers
      .map((id) => {
        const marker = QUALITY_MARKER_KEYS.find((m) => m.id === id);
        return marker ? t(marker.labelKey) : id;
      })
      .join(', ');
    const ruleName = `${t('records.agentReview.ruleNamePrefix')}: ${markerLabels}`;

    setPendingRule({
      ruleJson,
      category,
      severity,
      name: ruleName,
      description: t('records.agentReview.ruleDescription', { markers: markerLabels }),
    });
  };

  // Runs only after the user accepts the confirmation dialog — the sole
  // path that actually calls the API and writes the permanent rule.
  const handleConfirmCreateRule = async () => {
    if (!pendingRule || !projectId) return;

    try {
      setCreatingRule(true);

      await apiClient.alerts.createAlertRule({
        projectId: projectId,
        name: pendingRule.name,
        description: pendingRule.description,
        category: pendingRule.category,
        severity: pendingRule.severity,
        ruleJson: pendingRule.ruleJson,
        isEnabled: true,
      });

      toast.success(
        t('records.agentReview.ruleCreated'),
        t('records.agentReview.ruleCreatedMessage', { name: pendingRule.name })
      );
    } catch (err) {
      logError(
        'records.agentReview.create_alert_rule_failed',
        {
          component: 'AgentReviewSection',
          projectId,
          markerCount: selectedMarkers.length,
        },
        err instanceof Error ? err : undefined
      );
      toast.error(
        t('records.agentReview.createRuleError'),
        err instanceof Error ? err.message : t('common:common.errorUnknown')
      );
    } finally {
      setCreatingRule(false);
      setPendingRule(null);
    }
  };

  const handleOpenIncidentModal = () => {
    if (!recordId || !projectId) {
      toast.error(
        t('records.actions.incidentFormFailed', 'Failed to open incident form'),
        t(
          'records.actions.missingIncidentData',
          'Analysis or project context is missing for this record'
        )
      );
      return;
    }
    setShowIncidentModal(true);
  };

  // Confirmation-dialog focus trap (WCAG 2.1.2) — same pattern as
  // IncidentModal's own dialog.
  const ruleConfirmContainerRef = useFocusTrap<HTMLDivElement>(pendingRule !== null);

  useEffect(() => {
    if (!pendingRule) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !creatingRule) setPendingRule(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [pendingRule, creatingRule]);

  return (
    <>
      <Card data-testid="analysis-panel-review-notes">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-medium text-fg flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5" />
            {t('records.agentReview.title')}
          </h3>
          <Badge
            variant={
              computedStatus === 'flagged'
                ? 'danger'
                : computedStatus === 'reviewed'
                  ? 'success'
                  : 'warning'
            }
            size={computedStatus === 'reviewed' ? 'lg' : 'md'}
            icon={
              computedStatus === 'reviewed' ? (
                <Sparkles className="h-3.5 w-3.5" />
              ) : undefined
            }
            className={cn(
              computedStatus === 'reviewed' && 'shadow-glow-success animate-pulse-subtle'
            )}
          >
            {t(`records.agentReview.status.${computedStatus}`)}
          </Badge>
        </div>
        {loading ? (
          <div className="py-4">
            <SkeletonForm fields={6} />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Status description */}
            <div className="text-xs text-fg-muted bg-bg-subtle rounded-input px-3 py-2">
              {t(`records.agentReview.statusDescription.${computedStatus}`)}
            </div>

            {/* Quality Markers */}
            <div>
              <Label className="text-sm font-medium text-fg mb-3 block">
                {t('records.agentReview.qualityMarkers')}
              </Label>
              <div className="space-y-2">
                {QUALITY_MARKER_KEYS.map((marker) => (
                  <div key={marker.id} className="flex items-center gap-2">
                    <Checkbox
                      id={marker.id}
                      checked={selectedMarkers.includes(marker.id)}
                      onCheckedChange={() => handleMarkerToggle(marker.id)}
                    />
                    <Label htmlFor={marker.id} className="text-sm text-fg cursor-pointer">
                      {t(marker.labelKey)}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Overall Feedback */}
            <div>
              <Label
                htmlFor="overall-feedback"
                className="text-sm font-medium text-fg mb-2 block"
              >
                {t('records.agentReview.overallFeedback')}
              </Label>
              <Textarea
                id="overall-feedback"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder={t('records.agentReview.feedbackPlaceholder')}
                minRows={4}
                maxLength={2000}
              />
              <p className="mt-1 text-xs text-fg-muted">
                {t('records.agentReview.charactersCount', {
                  count: feedback.length,
                  max: 2000,
                })}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-between items-center gap-4">
              <div className="flex flex-wrap items-center gap-2">
                {selectedMarkers.length > 0 && projectId && canCreateAlertRule && (
                  <Button
                    onClick={handleRequestCreateRule}
                    variant="outline"
                    // Fixed min-width so this button and "Create incident"
                    // below stay the same width regardless of which locale's
                    // (longer or shorter) label is active — a narrower
                    // button here would make the pair look misaligned.
                    className="min-w-[180px]"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    {t(
                      'records.agentReview.createRuleFromPattern',
                      'Create Rule from Pattern'
                    )}
                  </Button>
                )}
                {selectedMarkers.length > 0 && recordId && projectId && (
                  <Button
                    onClick={handleOpenIncidentModal}
                    variant="outline"
                    className="min-w-[180px]"
                  >
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    {t('records.actions.createIncident', 'Create incident')}
                  </Button>
                )}
              </div>
              <div className="flex-1" />
              <Button
                onClick={handleSaveReview}
                isLoading={saving}
                disabled={saving || justSaved}
                size="sm"
                className={cn(
                  'min-w-[120px]',
                  justSaved && 'bg-success hover:bg-success'
                )}
              >
                {!saving &&
                  (justSaved ? (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  ))}
                {justSaved
                  ? t('records.agentReview.saved')
                  : t('records.agentReview.saveReview')}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create-rule confirmation — states what will be created before it
          exists, since a click here used to write a permanent, project-wide
          rule with no warning at all. */}
      {pendingRule && (
        <div
          ref={ruleConfirmContainerRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="rule-confirm-title"
          data-testid="rule-confirm-dialog"
          className="fixed inset-0 z-modal flex items-center justify-center bg-bg-void/50 p-4"
        >
          <Card
            data-testid="rule-confirm-card"
            className="w-full max-w-lg p-6 bg-bg-surface border-border"
          >
            <h2
              id="rule-confirm-title"
              className="text-base font-medium text-fg flex items-center gap-2 mb-2"
            >
              <Sparkles className="h-5 w-5" />
              {t('records.agentReview.confirmRuleDialog.title', 'Create Alert Rule')}
            </h2>
            <p className="text-sm text-fg-muted mb-4">
              {t(
                'records.agentReview.confirmRuleDialog.description',
                'This will create a permanent rule that applies to all future calls in this project.'
              )}
            </p>
            <div className="space-y-1.5 text-sm mb-6 bg-bg-subtle rounded-input px-3 py-2">
              <div>
                <span className="font-medium text-fg">
                  {t('records.agentReview.confirmRuleDialog.nameLabel', 'Rule name')}:
                </span>{' '}
                <span className="text-fg-muted">{pendingRule.name}</span>
              </div>
              <div>
                <span className="font-medium text-fg">
                  {t('records.agentReview.confirmRuleDialog.conditionLabel', 'Condition')}
                  :
                </span>{' '}
                <code className="text-fg-muted">
                  {describeCondition(pendingRule.ruleJson)}
                </code>
              </div>
              <div>
                <span className="font-medium text-fg">
                  {t('common:common.severity')}:
                </span>{' '}
                <span className="text-fg-muted">
                  {t(
                    `common:common.${pendingRule.severity}`,
                    SEVERITY_LABELS[pendingRule.severity]
                  )}
                </span>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                onClick={() => setPendingRule(null)}
                variant="outline"
                size="sm"
                disabled={creatingRule}
              >
                {t('common:common.cancel')}
              </Button>
              <Button
                onClick={() => void handleConfirmCreateRule()}
                isLoading={creatingRule}
                disabled={creatingRule}
                size="sm"
              >
                {t('common:common.confirm')}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Incident Modal — apiClient passed explicitly (the same one this
          section already resolved for embedded vs. standalone use), same
          precedent as RecordDetailInline's own IncidentModal/AnnotationModal
          — so it never reaches for its own Clerk-based client here. */}
      {recordId && projectId && (
        <IncidentModal
          analysisId={analysisId}
          recordId={recordId}
          projectId={projectId}
          isOpen={showIncidentModal}
          onClose={() => setShowIncidentModal(false)}
          onSuccess={() => {
            setShowIncidentModal(false);
          }}
          initialMarkers={selectedMarkers}
          apiClient={apiClient}
        />
      )}
    </>
  );
}
